import type {
  DonationChargeRequestDto,
  DonationChargeResponseDto,
  PreChargeBlockRequestDto,
  PreChargeBlockResponseDto,
} from '@signalvote/shared';
import { API_BASE_URL } from '../config';

/**
 * Imperative, unauthenticated donor-page POST helpers. The donor-facing path is
 * neutral (`/api/donations/{slug}/…`, no subsidiary segment); the subsidiary rides
 * the `X-Subsidiary` header the per-subsidiary origin's build supplies (ADR-052).
 * They are user-triggered on the donation form's Continue action, so they are plain
 * helpers, not React Query hooks. Each returns a typed result that exposes the HTTP
 * status discriminator the page's state machine needs.
 *
 * Neither request nor response carries a dollar figure or PII in the block path
 * (the server deliberately omits it), so the page never renders a $ from a block.
 */

/**
 * One bare-fetch POST both helpers share. Returns the raw HTTP status plus the
 * parsed body (only read on a 2xx), or status 0 on a network/parse failure — each
 * caller then maps that status through its own outcome ladder.
 */
async function postDonationJson(
  path: string,
  subsidiary: string,
  body: unknown,
): Promise<{ status: number; json: unknown | null }> {
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Subsidiary': subsidiary },
      body: JSON.stringify(body),
    });
    const json = response.ok ? await response.json() : null;
    return { status: response.status, json };
  } catch {
    return { status: 0, json: null };
  }
}

// ---------------------------------------------------------------------------
// block — POST /api/donations/{slug}/limit-check  (subsidiary → X-Subsidiary header)
// ---------------------------------------------------------------------------

export type BlockCheckResult =
  | { status: 'ok'; decision: PreChargeBlockResponseDto }
  | { status: 'notFound' }
  | { status: 'rateLimited' }
  | { status: 'error' };

export async function checkContributionLimit(
  subsidiary: string,
  slug: string,
  fields: PreChargeBlockRequestDto,
): Promise<BlockCheckResult> {
  const { status, json } = await postDonationJson(
    `/api/donations/${slug}/limit-check`,
    subsidiary,
    fields,
  );
  if (status === 404) return { status: 'notFound' };
  if (status === 429) return { status: 'rateLimited' };
  if (!json) return { status: 'error' };
  return { status: 'ok', decision: json as PreChargeBlockResponseDto };
}

// ---------------------------------------------------------------------------
// charge — POST /api/donations/{slug}/charge  (subsidiary → X-Subsidiary header)
// ---------------------------------------------------------------------------

/**
 * Charge outcomes, discriminated by the state the page must transition to:
 * - `created`  → 200 with clientSecret + publishableKey → mount Embedded Checkout.
 * - `blocked`  → 200 with blocked=true → surface the block (last-mile limit flip).
 * - `notReady` → 409 → the load↔submit race; flip the whole page to "unavailable".
 * - `invalid`  → 400 → attestation missing / below minimum (form-level).
 * - `notFound` → 404 · `rateLimited` → 429 · `error` → anything else.
 */
export type DonationChargeResult =
  | { status: 'created'; clientSecret: string; publishableKey: string }
  | { status: 'blocked'; block: PreChargeBlockResponseDto | null }
  | { status: 'notReady' }
  | { status: 'invalid' }
  | { status: 'notFound' }
  | { status: 'rateLimited' }
  | { status: 'error' };

/**
 * What the donor form should surface inline after a charge attempt. The page's
 * charge-outcome router (`DonationPage.handleChargeResult`) classifies every
 * `DonationChargeResult`: it drives the page-level transitions itself (mount
 * checkout, flip to unavailable) and returns to the form what belongs inline —
 * `none` when the page already navigated, a contribution-limit `block` (the
 * last-mile limit flip), or a retryable `error` message. This is the small
 * page→form path that lets a last-mile block reuse the form's existing block UI.
 */
export type ChargeFeedback =
  | { kind: 'none' }
  | { kind: 'blocked'; block: PreChargeBlockResponseDto | null }
  | { kind: 'error'; message: string };

export async function createDonationCharge(
  subsidiary: string,
  slug: string,
  fields: DonationChargeRequestDto,
): Promise<DonationChargeResult> {
  const { status, json } = await postDonationJson(
    `/api/donations/${slug}/charge`,
    subsidiary,
    fields,
  );
  if (status === 409) return { status: 'notReady' };
  if (status === 400) return { status: 'invalid' };
  if (status === 404) return { status: 'notFound' };
  if (status === 429) return { status: 'rateLimited' };
  if (!json) return { status: 'error' };

  const body = json as DonationChargeResponseDto;
  if (body.blocked) {
    return { status: 'blocked', block: body.block };
  }
  if (body.clientSecret && body.publishableKey) {
    return {
      status: 'created',
      clientSecret: body.clientSecret,
      publishableKey: body.publishableKey,
    };
  }
  return { status: 'error' };
}
